/**
 * @fileoverview Event handlers for the NotificationForm page.
 * @module pages/notificationForm/useNotificationFormHandlers
 */
import { useCallback } from "react";
import styles from "../../styles/NotificationForm.module.css";
import {
  ALLOWED_IMAGE_TYPES,
  MAX_IMAGE_SIZE_BYTES,
} from "./notificationFormConstants.js";
import { canCreateNotification } from "./notificationFormValidation.js";
import { submitNotification } from "./notificationFormSubmit.js";

/** Returns memoized NotificationForm handlers. */
const useNotificationFormHandlers = (ctx) => {
  const handleCheckboxChange = useCallback(
    (e, fieldName) => {
      ctx.setFormData((prev) => ({
        ...prev,
        [fieldName]: e.target.checked,
      }));
    },
    [ctx.setFormData],
  );

  const handleNotificationTypeChange = useCallback(
    (selectedOption) => {
      ctx.setFormData((prev) => ({
        ...prev,
        tipo: selectedOption,
        asunto: null,
        descripcion: null,
        imagenUrl: null,
      }));
      ctx.setImagePreview(null);
      ctx.setImageFile(null);
      ctx.setNewObjectId(null);
      ctx.pendingUploadedImageRef.current = null;
    },
    [
      ctx.setFormData,
      ctx.setImagePreview,
      ctx.setImageFile,
      ctx.setNewObjectId,
      ctx.pendingUploadedImageRef,
    ],
  );

  const handleDateChange = useCallback(
    (date) => {
      if (!date) {
        ctx.setFormData((prev) => ({ ...prev, fechaAltaSac: null }));
        return;
      }
      ctx.setFormData((prev) => ({
        ...prev,
        fechaAltaSac: date.format("YYYY-MM-DD"),
      }));
      ctx.setIsCalendarOpen(false);
    },
    [ctx.setFormData, ctx.setIsCalendarOpen],
  );

  const handleImageChange = useCallback(
    (event) => {
      const file = event.target.files[0];
      if (!file) {
        return;
      }
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        ctx.setError(ctx.t("pages.buyerCompanyForm.logoFileTypeError"));
        return;
      }
      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        ctx.setError(ctx.t("pages.buyerCompanyForm.logoFileSizeError"));
        return;
      }
      ctx.setImageFile(file);
      ctx.pendingUploadedImageRef.current = null;
      ctx.setImagePreview(URL.createObjectURL(file));
    },
    [ctx],
  );

  const onDragEnter = useCallback(
    (_e) => ctx.imageRef.current?.classList.add(styles.dragOver),
    [ctx.imageRef],
  );
  const onDragLeave = useCallback(
    (_e) => ctx.imageRef.current?.classList.remove(styles.dragOver),
    [ctx.imageRef],
  );
  const onDrop = useCallback(
    (_e) => ctx.imageRef.current?.classList.remove(styles.dragOver),
    [ctx.imageRef],
  );

  const handleAsuntoChange = useCallback(
    (e) => ctx.setFormData((prev) => ({ ...prev, asunto: e.target.value })),
    [ctx.setFormData],
  );

  const handleDescripcionChange = useCallback(
    (e) =>
      ctx.setFormData((prev) => ({ ...prev, descripcion: e.target.value })),
    [ctx.setFormData],
  );

  const isCreateEnabled = useCallback(
    () => canCreateNotification(ctx.formData, ctx.imageFile),
    [ctx.formData, ctx.imageFile],
  );

  const handleCreateNotification = useCallback(async () => {
    await submitNotification(ctx);
  }, [ctx]);

  return {
    handleCheckboxChange,
    handleNotificationTypeChange,
    handleDateChange,
    handleImageChange,
    onDragEnter,
    onDragLeave,
    onDrop,
    handleAsuntoChange,
    handleDescripcionChange,
    isCreateEnabled,
    handleCreateNotification,
  };
};

export default useNotificationFormHandlers;
